// PLAN.md §3.1 — first-launch handle picker.

import { useState } from 'react'
import { validateHandle } from '@trading-grounds/shared'
import { Button } from './ported/index.js'

export type HandlePickerProps = {
  onSubmit: (display_handle: string) => void
  /** Optional error from a prior submit (e.g. "handle taken"). */
  error?: string | null
}

const REASON_COPY: Record<string, string> = {
  too_short: 'Pick a handle that is at least 3 characters.',
  too_long: '15 characters max.',
  invalid_chars: 'Letters, numbers, dash, underscore only.',
  reserved: "That one's reserved — try another.",
  profane: "Let's keep it civil.",
}

export function HandlePicker({ onSubmit, error }: HandlePickerProps) {
  const [value, setValue] = useState('')
  const [localErr, setLocalErr] = useState<string | null>(null)

  function submit() {
    const trimmed = value.trim()
    const v = validateHandle(trimmed)
    if (!v.ok) {
      setLocalErr(REASON_COPY[v.reason] ?? 'Invalid handle.')
      return
    }
    setLocalErr(null)
    onSubmit(trimmed)
  }

  const errorText = localErr ?? error ?? null

  return (
    <main className="min-h-screen flex items-center justify-center px-4">
      <div className="w-full max-w-sm flex flex-col gap-4 bg-white rounded-2xl border border-light-grey shadow-big p-6">
        <div className="flex flex-col gap-1">
          <span className="text-logo">Trading Grounds</span>
          <span className="text-title text-black leading-tight">Pick a handle</span>
          <span className="text-label text-grey">3–15 characters · letters, numbers, dash, underscore</span>
        </div>
        <input
          autoFocus
          type="text"
          inputMode="text"
          autoComplete="off"
          spellCheck={false}
          value={value}
          onChange={(e) => {
            setValue(e.target.value)
            if (localErr) setLocalErr(null)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit()
          }}
          maxLength={15}
          placeholder="e.g. nightowl"
          className="h-11 px-4 rounded-full bg-light-grey text-body text-black tabular-nums outline-none focus:ring-2 focus:ring-black/40"
        />
        {errorText && <span className="text-label text-red">{errorText}</span>}
        <Button variant="primary" size="md" onClick={submit}>
          Join the field
        </Button>
      </div>
    </main>
  )
}
